import { runInAction } from 'mobx';

import { LoadingStageModel } from 'shared/models/loadingStage';

const POLLING_TIMEOUT = 2000;

export type TimeoutHandlerType = () => Promise<BaseResponse> | BaseResponse;

export class PollingModel {
  readonly pollingStage: LoadingStageModel = new LoadingStageModel();

  protected timeout: ReturnType<typeof setInterval> | null = null;

  private activePromise: Promise<BaseResponse> | null = null;
  private resolveActive: ((response: BaseResponse) => void) | null = null;
  private pollingAttemptsCount = 0;
  private callbackInProgress = false;
  private pollingRun = 0;
  private cb: TimeoutHandlerType;
  private cbOnMaxAttempts: TimeoutHandlerType | null;
  private readonly defaultMaxAttemptsCount: number;
  private maxAttemptsCount: number;

  constructor({
    cb,
    cbOnMaxAttempts = null,
    maxAttemptsCount = 60
  }: {
    cb: TimeoutHandlerType;
    cbOnMaxAttempts?: TimeoutHandlerType | null;
    maxAttemptsCount?: number;
  }) {
    this.cb = cb;
    this.cbOnMaxAttempts = cbOnMaxAttempts;
    this.defaultMaxAttemptsCount = maxAttemptsCount;
    this.maxAttemptsCount = maxAttemptsCount;

    this.stopPolling = this.stopPolling.bind(this);
    this.startPolling = this.startPolling.bind(this);
  }

  stopPolling(): void {
    this.maxAttemptsCount = this.defaultMaxAttemptsCount;

    if (!this.timeout) {
      return;
    }

    clearInterval(this.timeout);
    this.timeout = null;
    this.pollingRun++;
    this.callbackInProgress = false;
    this.pollingAttemptsCount = 0;
    this.activePromise = null;
    this.resolveActive = null;
    this.pollingStage.reset();
  }

  private handleCallback = async (
    resolve: (response: BaseResponse) => void
  ) => {
    if (this.callbackInProgress) {
      return;
    }

    if (this.pollingAttemptsCount >= this.maxAttemptsCount) {
      this.cbOnMaxAttempts?.();
      this.stopPolling();

      resolve({
        isError: true
      });
      return;
    }

    this.callbackInProgress = true;
    const run = this.pollingRun;
    this.pollingStage.loading();

    let isError: boolean;

    try {
      ({ isError } = await this.cb());
    } finally {
      if (run === this.pollingRun) {
        this.callbackInProgress = false;
      }
    }

    if (run !== this.pollingRun) {
      return;
    }

    this.pollingAttemptsCount++;

    if (!isError) {
      runInAction(() => {
        this.stopPolling();
      });

      resolve({
        isError: false
      });
    }
  };

  /**
   * @param maxAttemptsCount — лимит попыток для этого запуска, если опросу нужно ждать дольше, чем по умолчанию
   */
  startPolling(maxAttemptsCount?: number): Promise<BaseResponse> {
    if (this.activePromise) {
      return this.activePromise;
    }

    this.maxAttemptsCount = maxAttemptsCount ?? this.defaultMaxAttemptsCount;

    this.activePromise = new Promise<BaseResponse>((resolve) => {
      this.resolveActive = resolve;
      if (this.timeout) {
        this.stopPolling();
      }

      this.timeout = setInterval(
        () => this.handleCallback(resolve),
        POLLING_TIMEOUT
      );
    });

    return this.activePromise;
  }

  pollNow(): void {
    if (this.resolveActive) {
      void this.handleCallback(this.resolveActive);
    }
  }
}
